import api from './api';

// --- Categories ---
export const getTrainingCategories = (params) =>
  api.get('/v1/training-categories', { params });

export const getTrainingCategory = (id) =>
  api.get(`/v1/training-categories/${id}`);

export const createTrainingCategory = (data) =>
  api.post('/v1/training-categories', data);

export const updateTrainingCategory = (id, data) =>
  api.put(`/v1/training-categories/${id}`, data);

export const deleteTrainingCategory = (id) =>
  api.delete(`/v1/training-categories/${id}`);

// --- Trainings ---
export const getTrainings = (params) =>
  api.get('/v1/trainings', { params });

export const getTraining = (id) =>
  api.get(`/v1/trainings/${id}`);

export const createTraining = (data) =>
  api.post('/v1/trainings', data);

export const updateTraining = (id, data) =>
  api.put(`/v1/trainings/${id}`, data);

export const archiveTraining = (id) =>
  api.post(`/v1/trainings/${id}/archive`);

export const unpublishTraining = (id) =>
  api.post(`/v1/trainings/${id}/unpublish`);

export const getTrainingEvaluationResults = (id, params) =>
  api.get(`/v1/trainings/${id}/evaluation-results`, { params });

// --- Content items ---
export const listTrainingContent = (trainingId) =>
  api.get(`/v1/trainings/${trainingId}/content`);

export const addTrainingContent = (trainingId, data) =>
  api.post(`/v1/trainings/${trainingId}/content`, data);

export const updateTrainingContent = (trainingId, itemId, data) =>
  api.put(`/v1/trainings/${trainingId}/content/${itemId}`, data);

export const removeTrainingContent = (trainingId, itemId) =>
  api.delete(`/v1/trainings/${trainingId}/content/${itemId}`);

// --- Approval flow ---
export const getTrainingApprovalSteps = (id) =>
  api.get(`/v1/trainings/${id}/approval`);

export const submitTrainingForApproval = (id, approverUserIds) =>
  api.post(`/v1/trainings/${id}/submit-for-approval`, { approverUserIds });

export const approveTraining = (id, comment) =>
  api.post(`/v1/trainings/${id}/approval/approve`, { comment });

export const requestTrainingRevision = (id, comment) =>
  api.post(`/v1/trainings/${id}/approval/request-revision`, { comment });

export const rejectTraining = (id, comment) =>
  api.post(`/v1/trainings/${id}/approval/reject`, { comment });

// --- Quiz ---
export const getTrainingQuizConfig = (trainingId) =>
  api.get(`/v1/trainings/${trainingId}/quiz`);

export const upsertTrainingQuizConfig = (trainingId, data) =>
  api.put(`/v1/trainings/${trainingId}/quiz`, data);

export const listTrainingQuizQuestions = (trainingId) =>
  api.get(`/v1/trainings/${trainingId}/quiz/questions`);

export const addTrainingQuizQuestion = (trainingId, data) =>
  api.post(`/v1/trainings/${trainingId}/quiz/questions`, data);

export const updateTrainingQuizQuestion = (trainingId, questionId, data) =>
  api.put(`/v1/trainings/${trainingId}/quiz/questions/${questionId}`, data);

export const deleteTrainingQuizQuestion = (trainingId, questionId) =>
  api.delete(`/v1/trainings/${trainingId}/quiz/questions/${questionId}`);

// --- Assignments ---
export const listTrainingAssignments = (trainingId, params) =>
  api.get(`/v1/training-assignments/by-training/${trainingId}`, { params });

export const assignTraining = (trainingId, data) =>
  api.post(`/v1/training-assignments/by-training/${trainingId}`, data);

export const unassignTraining = (assignmentId) =>
  api.delete(`/v1/training-assignments/${assignmentId}`);

export const batchUnassignTraining = (assignmentIds) =>
  api.post('/v1/training-assignments/batch-unassign', { assignmentIds });

export const listUnassignedUsers = (trainingId, params) =>
  api.get(`/v1/training-assignments/by-training/${trainingId}/unassigned-users`, { params });

export const extendTrainingAssignment = (assignmentId, newDueDate) =>
  api.patch(`/v1/training-assignments/${assignmentId}/extend`, { newDueDate });

export const grantBonusAttempts = (assignmentId) =>
  api.post(`/v1/training-assignments/${assignmentId}/grant-bonus`);

export const getPendingTrainingApprovals = (params) =>
  api.get('/v1/trainings/pending-approvals', { params });

// --- Learner ("my") ---
export const getMyTrainings = (params) =>
  api.get('/v1/my-trainings', { params });

export const getMyTraining = (assignmentId) =>
  api.get(`/v1/my-trainings/${assignmentId}`);

// 30-second heartbeat while a user consumes a content item.
// payload: { contentItemId, lastPositionSeconds?, watchedSeconds?, viewedPages?, clientCompleted? }
export const postMyTrainingProgress = (assignmentId, payload) =>
  api.post(`/v1/my-trainings/${assignmentId}/progress`, payload);

// --- Learner quiz session ---
export const startQuizAttempt = (assignmentId) =>
  api.post(`/v1/my-trainings/${assignmentId}/quiz/start`);

export const getQuizAttempt = (attemptId) =>
  api.get(`/v1/my-trainings/quiz/attempts/${attemptId}`);

export const submitQuizAttempt = (attemptId, answers) =>
  api.post(`/v1/my-trainings/quiz/attempts/${attemptId}/submit`, { answers });

// Autosave a single question's selections so the learner can resume later
// without losing progress (QA R114).
export const saveQuizAnswer = (attemptId, questionId, selectedOptionIds) =>
  api.post(`/v1/my-trainings/quiz/attempts/${attemptId}/save-answer`, {
    questionId,
    selectedOptionIds,
  });

export const reportQuizTabBlur = (attemptId) =>
  api.post(`/v1/my-trainings/quiz/attempts/${attemptId}/tab-blur`);

// Post-completion evaluation (1 per assignment).
export const submitTrainingEvaluation = (assignmentId, payload) =>
  api.post(`/v1/my-trainings/${assignmentId}/evaluation`, payload);

// --- Certificates ---
export const getTrainingCertificateForAssignment = (assignmentId) =>
  api.get(`/v1/training-certificates/by-assignment/${assignmentId}`);

export const issueTrainingCertificate = (assignmentId) =>
  api.post(`/v1/training-certificates/issue/${assignmentId}`);

// Public verification (no auth).
export const verifyTrainingCertificate = (code) =>
  api.get(`/v1/public/training-certificates/verify/${code}`);

// --- Gamification ---
export const getMyBadges = () =>
  api.get('/v1/training-gamification/my-badges');

export const getTrainingLeaderboard = (params) =>
  api.get('/v1/training-gamification/leaderboard', { params });

// --- Certificate Templates ---
export const getCertificateTemplates = (params) =>
  api.get('/v1/certificate-templates', { params });

export const getCertificateTemplate = (id) =>
  api.get(`/v1/certificate-templates/${id}`);

export const createCertificateTemplate = (data) =>
  api.post('/v1/certificate-templates', data);

export const updateCertificateTemplate = (id, data) =>
  api.put(`/v1/certificate-templates/${id}`, data);

export const deleteCertificateTemplate = (id) =>
  api.delete(`/v1/certificate-templates/${id}`);

// --- Assignment Rules ---
export const getAssignmentRules = (params) =>
  api.get('/v1/training-assignment-rules', { params });

export const createAssignmentRule = (data) =>
  api.post('/v1/training-assignment-rules', data);

export const updateAssignmentRule = (id, data) =>
  api.put(`/v1/training-assignment-rules/${id}`, data);

export const deleteAssignmentRule = (id) =>
  api.delete(`/v1/training-assignment-rules/${id}`);

export const evaluateAssignmentRules = () =>
  api.post('/v1/training-assignment-rules/evaluate');

// --- Audit Log ---
export const getTrainingAuditLog = (params) =>
  api.get('/v1/training-audit', { params });

// --- Manual nudge ---
export const nudgeTrainingAssignment = (assignmentId) =>
  api.post(`/v1/training-assignments/${assignmentId}/nudge`);
